import React, { useState, useEffect } from "react";
import { ModalBody, ModalHeader, TabContent, TabPane } from "reactstrap";
import { ItemListTabs, VbzButton, VbzModal, VbzTabBar } from "@smiles/voebiz-ui-kit";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useForm } from "react-hook-form";

import { useUpdateForm } from "../../hooks/useUpdateForm";
import { StateType } from "../../hooks/types";

import { checkZipCode } from "../../services";

import { VoeBizLogo } from "../../assets";
import ModalErrorBody from "./ModalErrorBody/index";
import ModalSuccessBody from "./ModalSuccessBody/index";
import ModalBodyLoader from "./ModalBodyLoader/index";
import AddressForm from "./AddressForm";
import AdminForm from "./AdminForm";
import CompanyForm from "./CompanyForm";
import { formatCEP, regexPhoneFormatter } from "../../utils/documentUtils";

export default function UpdateModal({ isOpen, toggle, state }: { isOpen: boolean; toggle: any; state: StateType }) {
  const { isLoading, isUpdatingData, isError, isSuccess, onFormSubmit, tryAgain } = useUpdateForm(state);
  type Tabs = "endereco" | "empresa" | "admin";
  const [activeTab, setActiveTab] = useState<Tabs>("empresa");
  const [isZipCodeValid, setIsZipCodeValid] = useState(true);
  const { apiDomain } = useFlags();

  const {
    control,
    errors,
    handleSubmit,
    watch,
    getValues,
    setValue,
    setError,
    formState: { dirtyFields, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      CNPJ: state.member.CNPJ,
      socialReason: state.member.socialReason,
      customerCategory: "",
      numberOfEmployees: state.member.numberOfEmployees,
      annualBudgetforTravel: state.member.annualBudgetforTravel,
      DOcumentNumber: state.member.DOcumentNumber,
      email: state.member.email,
      homePhoneNumber1: regexPhoneFormatter(`${state.member.homePhoneDDDNumber1}${state.member.homePhoneNumber1}`),
      celPhoneNumber1: regexPhoneFormatter(`${state.member.celPhoneDDDNumber1}${state.member.celPhoneNumber1}`),
      zipCode: formatCEP(state.member.address.zipCode),
      streetAddress: state.member.address.streetAddress,
      state: state.member.address.state,
      city: state.member.address.city,
      streetAddressNumber: state.member.address.streetAddressNumber,
      complement: state.member.address.complement,
      mainActivity: state.dropdowns.mainActivity?.items.find((item) => state.member.mainActivity === item.id && item)
        ?.description,
      fantasyName: state.member.fantasyName,
      adminName: `${state.member.firstName} ${state.member.lastName}`,
      occupation: state.dropdowns.occupation?.items.find((item) => state.member.occupation === item.id && item)
        ?.description,
    },
  });

  const tabs: ItemListTabs[] = [
    {
      id: "empresa",
      key: "empresa",
      label: "Empresa",
      active: activeTab === "empresa",
      onClick: () => setActiveTab("empresa"),
    },
    {
      id: "endereco",
      key: "endereco",
      label: "Endereço",
      active: activeTab === "endereco",
      onClick: () => setActiveTab("endereco"),
    },
    {
      id: "admin",
      key: "admin",
      label: "Administrador da Conta",
      active: activeTab === "admin",
      onClick: () => setActiveTab("admin"),
    },
  ];

  useEffect(() => {
    setValue("city", "");
    setValue("state", "");
    setValue("streetAddress", "");
    setIsZipCodeValid(false);

    if (formatCEP(getValues("zipCode")).length === 10) {
      checkZipCode(apiDomain, getValues("zipCode").replace(/[^0-9]/g, "")).then(({ City, State, StreetName }) => {
        if (City) {
          setValue("city", City);
          setValue("state", State);
          setValue("streetAddress", StreetName);
          setIsZipCodeValid(true);
        }
      });
    }
  }, [watch("zipCode")]);

  const closeSuccessModal = () => {
    window.location.href = `/`;
  };

  return (
    <VbzModal isOpen={isOpen} type="right" desktopSize="small" toggle={() => toggle()} defaultHeader={false}>
      {!isLoading && (
        <ModalHeader className="vbz-profile-modal-header" tag="div">
          <img className="hidden" src={VoeBizLogo} width={86} height={21} alt="Logo" />
          <button
            id="btn_close"
            type="button"
            className="vbz-btn-close"
            data-dismiss="modal"
            aria-label="Close"
            onClick={() => toggle()}
          >
            <i className="material-icons">&#xe5cd;</i>
          </button>
        </ModalHeader>
      )}

      {isError && <ModalErrorBody onClick={tryAgain} />}
      {isSuccess && <ModalSuccessBody onClick={closeSuccessModal} />}
      {isLoading && <ModalBodyLoader />}

      {isUpdatingData && (
        <ModalBody className="vbz-profile-tab-content">
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <div className="vbz-modal-header">
              <h4>Atualizar dados da conta</h4>
            </div>
            <div className="content-wrapper">
              <VbzTabBar list={tabs} />
              <TabContent activeTab={activeTab}>
                <TabPane tabId="empresa" className="vbz-profile-tab-content">
                  <CompanyForm control={control} errors={errors} state={state} />
                </TabPane>
                <TabPane tabId="endereco" className="vbz-profile-tab-content">
                  <AddressForm control={control} errors={errors} />
                </TabPane>
                <TabPane tabId="admin" className="vbz-profile-tab-content">
                  <AdminForm control={control} errors={errors} state={state} />
                </TabPane>
              </TabContent>
            </div>
            <div className="vbz-profile-tab-content-button">
              <VbzButton
                id="submit"
                className="vbz-update-button"
                color="primary"
                text="Atualizar meus dados"
                type="submit"
                disabled={!(Object.keys(dirtyFields).length && isValid && isZipCodeValid)}
              />
            </div>
          </form>
        </ModalBody>
      )}
    </VbzModal>
  );
}
